import { executeListOps } from '../graph/listOps';
import { getListItems } from '../graph/lists';
import { parseBudgets, serializeBudgets, type BudgetMap } from '../audit/budgets';
import { LISTS } from './listNames';

/**
 * StoreBudgets (B8, owner rulings 2026-10-25): one row per store per year,
 * every KPI's 12 monthly budgets as JSON (src/audit/budgets.ts). Admins edit
 * any store's, DMs their own stores' (the server checks — api/src/
 * writePolicy.ts); everyone who sees a store reads its budgets.
 */

interface BudgetFields {
  Title: string;
  StoreId: number;
  BudgetYear: number;
  Budgets: string;
  UpdatedBy: string;
}

export interface StoreBudgetRow {
  /** null = not saved yet. */
  id: string | null;
  storeId: number;
  year: number;
  budgets: BudgetMap;
  updatedBy: string;
  updatedAt: string;
  /** Sent back as If-Match: a save over someone else's newer change is refused (412), never silently reverts it. */
  eTag?: string;
}

function toRow(i: { id: string; fields: Partial<BudgetFields>; lastModifiedDateTime: string; eTag?: string }): StoreBudgetRow | null {
  const storeId = Number(i.fields.StoreId);
  const year = Number(i.fields.BudgetYear);
  if (!Number.isInteger(storeId) || !Number.isInteger(year)) return null;
  return {
    id: i.id,
    storeId,
    year,
    budgets: parseBudgets(i.fields.Budgets),
    updatedBy: i.fields.UpdatedBy ?? '',
    updatedAt: i.lastModifiedDateTime,
    ...(i.eTag ? { eTag: i.eTag } : {}),
  };
}

/** One row per store × year: two rows made by a race keep the newest-id one. */
export function onePerStoreYear(rows: readonly StoreBudgetRow[]): StoreBudgetRow[] {
  const best = new Map<string, StoreBudgetRow>();
  for (const r of rows) {
    const key = `${r.storeId}:${r.year}`;
    const cur = best.get(key);
    if (!cur || Number(r.id) > Number(cur.id)) best.set(key, r);
  }
  return [...best.values()];
}

/** Every visible store's budgets for one year (BudgetYear is indexed). */
export async function getBudgetsForYear(year: number): Promise<StoreBudgetRow[]> {
  const items = await getListItems<BudgetFields>(LISTS.storeBudgets, { filter: `fields/BudgetYear eq ${year}` });
  return onePerStoreYear(items.map(toRow).filter((r): r is StoreBudgetRow => r !== null));
}

/** Several years at once (an Analytics range can span two). */
export async function getBudgetsForYears(years: readonly number[]): Promise<StoreBudgetRow[]> {
  const all = await Promise.all([...new Set(years)].map((y) => getBudgetsForYear(y)));
  return all.flat();
}

/** One store's rows, every year (StoreId is indexed). */
export async function getStoreBudgets(storeId: number): Promise<StoreBudgetRow[]> {
  const items = await getListItems<BudgetFields>(LISTS.storeBudgets, { filter: `fields/StoreId eq ${storeId}` });
  return onePerStoreYear(items.map(toRow).filter((r): r is StoreBudgetRow => r !== null));
}

export interface BudgetSave {
  storeId: number;
  storeNumber: string;
  year: number;
  /** The existing row's id, or null to create it. */
  id: string | null;
  /** The existing row's eTag as read (see StoreBudgetRow.eTag). */
  eTag?: string;
  budgets: BudgetMap;
}

/**
 * Save store-year rows (creates or updates). Returns the store ids that
 * failed with why — a partial save is reported, never hidden.
 */
export async function saveBudgets(saves: readonly BudgetSave[]): Promise<{ failed: { storeId: number; reason: string }[] }> {
  const responses = await executeListOps(
    saves.map((s, i) => {
      const Budgets = serializeBudgets(s.budgets);
      return s.id
        ? {
            id: `b-${i}`,
            method: 'PATCH' as const,
            list: LISTS.storeBudgets,
            itemId: s.id,
            fields: { Budgets },
            ...(s.eTag ? { ifMatch: s.eTag } : {}),
          }
        : {
            id: `b-${i}`,
            method: 'POST' as const,
            list: LISTS.storeBudgets,
            fields: { Title: `${s.storeNumber} ${s.year}`, StoreId: s.storeId, BudgetYear: s.year, Budgets },
          };
    }),
  );
  const failed: { storeId: number; reason: string }[] = [];
  saves.forEach((s, i) => {
    const r = responses.get(`b-${i}`);
    if (!r || r.status < 200 || r.status >= 300) {
      const body = r?.body as { error?: { message?: string } } | undefined;
      const reason =
        r?.status === 412
          ? 'someone else changed this store’s budgets meanwhile — check the numbers now shown, then save again'
          : (body?.error?.message ?? `Error ${r?.status ?? '?'}`);
      failed.push({ storeId: s.storeId, reason });
    }
  });
  return { failed };
}
